import { mande, type MandeError } from 'mande';
import { defineStore } from 'pinia';
import { toRaw } from 'vue';
import { dataURLtoFile } from '@/utils';
import { fetchWithTimeout, isTransportError } from '@/utils/networkUtils';
import { logToBetterStack, heapMB } from '@/utils/logger';
import { track } from '@/lib/usage';

import { useStore as useReadingStore } from './reading';
import type { WizardState } from './wizard';

const PHOTO_UPLOAD_TIMEOUT_MS = 30_000;
// Cap retained submitted items. The queue UI only ever shows the most recent
// 20 (ReadingsQueue.vue), so anything beyond this is invisible and only costs
// memory. Pending/errored items are never pruned — they still need to be sent.
const MAX_SUBMITTED_RETAINED = 20;

export interface WizardFullState extends WizardState {
  selectedAccount: ETNAccount;
  selectedAsset: ETNAsset;
}

export interface QueueItem {
  id: string;
  status: 'pending' | 'submitting' | 'submitted' | 'error';
  error: string | null;
  photoS3Key: string | null;
  // Set once the reading is persisted server-side. Lets the detail view fetch
  // the photo from S3 on demand instead of retaining the base64 in memory.
  readingId: string | null;
  addedAt: string;
  state: WizardFullState;
}

interface QueueState {
  queue: QueueItem[];
}

interface PhotoUploadResponse {
  ok: boolean;
  message?: string;
}

export const useStore = defineStore('queue', {
  state: (): QueueState => {
    return {
      queue: []
    };
  },
  getters: {
    queueItemById:
      (state: QueueState) =>
      (id: string): QueueItem | undefined => {
        return state.queue.find((item) => item.id === id);
      },
    submittingQueueItems: (state: QueueState) => {
      return state.queue.filter((item) => item.status === 'submitting');
    }
  },
  actions: {
    addWizardToQueue(wizard: WizardFullState) {
      const id = Math.random().toString(36).substring(7);

      const fullWizard = {
        accountId: wizard.accountId,
        assetId: wizard.assetId,
        overrideDate: wizard.overrideDate,
        overrideUnits: wizard.overrideUnits,
        photoData: toRaw(wizard.photoData),
        reading: toRaw(wizard.reading),
        selectedAccount: toRaw(wizard.selectedAccount),
        selectedAsset: toRaw(wizard.selectedAsset)
      };

      this.queue.push({
        id,
        status: 'pending',
        addedAt: new Date().toISOString(),
        photoS3Key: null,
        readingId: null,
        error: null,
        state: fullWizard
      });

      return id;
    },
    // Drop the base64 photo from a submitted item (it now lives in S3, reachable
    // via readingId) and trim the retained-submitted history. This is what keeps
    // the queue — and the heap/IndexedDB it's mirrored into — bounded.
    pruneSubmitted() {
      const submitted = this.queue.filter((item) => item.status === 'submitted');
      const toDrop = new Set(
        submitted
          .sort((a, b) => new Date(a.addedAt).getTime() - new Date(b.addedAt).getTime())
          .slice(0, Math.max(0, submitted.length - MAX_SUBMITTED_RETAINED))
          .map((item) => item.id)
      );

      this.queue = this.queue.filter((item) => !toDrop.has(item.id));
    },
    // Run once after rehydrating from localforage. Clears base64 photos left in
    // already-submitted items by pre-fix builds (the historical memory bloat)
    // and trims the retained history, so an existing heavy queue is compacted on
    // first load rather than only going forward.
    reconcileAfterRehydrate() {
      this.queue.forEach((item) => {
        // A submit interrupted by a tab kill can persist as 'submitting', which
        // processPendingQueueItems never retries — re-queue it as 'pending' so
        // it isn't stuck forever. (May re-send a reading the server already
        // accepted; an occasional duplicate beats a silently lost reading.)
        if (item.status === 'submitting') {
          item.status = 'pending';
          item.error = null;
        }
        // Drop the base64 only for submitted items whose photo is safely in S3
        // (photoS3Key set). Keep it for pending/error and for failed-upload
        // submits (no S3 copy).
        if (item.status === 'submitted' && item.photoS3Key && item.state.photoData.dataUrl) {
          item.state.photoData = { dataUrl: '', format: '' };
        }
      });
      this.pruneSubmitted();
    },
    async processPendingQueueItems(lastId?: string) {
      const pendingItems = this.queue.filter((item) => item.status === 'pending');

      // Process one at a time
      if (pendingItems.length > 0) {
        console.log(`Processing ${pendingItems[0].id}`, pendingItems[0]);
        if (lastId === pendingItems[0].id) {
          console.log('Tried to process the same item twice');
          return;
        }
        await this.processQueueItem(pendingItems[0].id);

        // Process the next one
        this.processPendingQueueItems(pendingItems[0].id);
      }
    },
    async uploadPhoto(queueId: string): Promise<PhotoUploadResponse> {
      const queueItem = this.queue.find((item) => item.id === queueId);
      if (!queueItem) {
        return { ok: false, message: 'No queue item found' };
      }

      const photoData = queueItem.state.photoData;
      if (!photoData.dataUrl || !photoData.format) {
        return { ok: false, message: 'No photo taken' };
      }

      const readingStore = useReadingStore();

      // Get the upload URL
      const photoUploadData = await readingStore.getPhotoUploadUrl(photoData.format);
      if (!photoUploadData?.uploadUrl) {
        return { ok: false, message: 'Could not get upload url' };
      }

      try {
        const photoBinary = dataURLtoFile(photoData.dataUrl, 'photo');

        const response = await fetchWithTimeout(
          (signal) =>
            fetch(photoUploadData.uploadUrl, {
              method: 'PUT',
              headers: {
                'Content-Type': `image/${photoData.format}`
              },
              body: photoBinary,
              signal
            }),
          PHOTO_UPLOAD_TIMEOUT_MS
        );

        // fetch resolves even on a non-2xx S3 response (e.g. an expired/invalid
        // presigned URL → 403, or 413). Only record the s3Key once the PUT is
        // verified successful — otherwise downstream logic would drop the local
        // base64 for a photo that was never actually uploaded.
        if (!response.ok) {
          return { ok: false, message: `Photo upload failed: ${response.status}` };
        }

        queueItem.photoS3Key = photoUploadData.s3Key;

        return { ok: true };
      } catch (e) {
        // Re-throw transport errors so processQueueItem can mark the item pending
        // for auto-retry; non-transport errors (rare for an S3 PUT — usually 403/400
        // for a bad presigned URL) keep the swallow-and-continue behaviour so a
        // photo upload failure doesn't block the reading submission itself.
        if (isTransportError(e)) throw e;
        return { ok: false };
      }
    },
    async processQueueItem(queueId: string) {
      const queueItem = this.queue.find((item) => item.id === queueId);
      if (!queueItem) {
        return false;
      }

      try {
        queueItem.status = 'submitting';

        // Upload the photo
        const photoUploadResponse = await this.uploadPhoto(queueId);
        if (!photoUploadResponse.ok) {
          console.log('Error uploading photo', photoUploadResponse.message);
        }

        // Submit the reading
        const readingStore = useReadingStore();
        const reading = queueItem.state.reading;

        const etnReading: Omit<ETNReading, '_id' | 'companyId' | 'entityId'> = {
          registerId: reading.registerId,
          value: reading.value!,
          submittedAt: new Date(reading.submittedAt).toISOString(),
          type: 'A',
          isReset: reading.isReset,
          units: reading.units,
          factor: reading.factor,
          note: queueItem.state.reading.note,
          s3Key: photoUploadResponse.ok && queueItem.photoS3Key ? queueItem.photoS3Key : undefined,
          source: 'app',
          accountId: queueItem.state.selectedAccount._id
        };

        const readingId = await readingStore.submitReading(etnReading);

        queueItem.readingId = readingId;
        queueItem.status = 'submitted';

        // Release the base64 photo only once it's safely in S3 (photoS3Key set)
        // — the detail view re-fetches it via readingId on demand. If the upload
        // failed (no photoS3Key), keep the base64: it's the only copy, and the
        // reading still submitted successfully, so dropping it would lose the
        // evidence photo entirely.
        if (queueItem.photoS3Key) {
          queueItem.state.photoData = { dataUrl: '', format: '' };
        }

        this.pruneSubmitted();

        logToBetterStack('reading_submitted', {
          queueItems: this.queue.length,
          photosRetained: this.queue.filter((i) => i.state.photoData.dataUrl).length,
          heapMB: heapMB()
        });
      } catch (e) {
        if (isTransportError(e)) {
          // Network/timeout failure during submit: expected operating condition for
          // a field app on poor signal. Leave the item in the queue so App.vue's
          // networkStatusChange listener can retry on reconnect, and log at info
          // level so we don't spam Sentry/error reporting with non-bugs.
          console.log('Transport failure during reading submit — will retry on reconnect', e);
          queueItem.status = 'pending';
          queueItem.error = null;
          return;
        }

        console.error('Error processing queue item', e);
        queueItem.status = 'error';
        queueItem.error = (e as Error).message;

        if ((e as MandeError).response) {
          const mandeError = e as MandeError;
          console.error('mandeError response', mandeError.response);
          console.error('mandeError body', mandeError.body);

          if (mandeError.body && mandeError.body.message) {
            if (
              mandeError.body.message === 'Form validation error' &&
              !Array.isArray(mandeError.body.formMessages)
            ) {
              queueItem.error = `${mandeError.body.message}: ${mandeError.body.formMessages?.value}`;
            } else if (
              mandeError.body.message === 'Form validation error' &&
              Array.isArray(mandeError.body.formMessages)
            ) {
              queueItem.error = `${mandeError.body.message}: ${mandeError.body.formMessages.map((m: { value: string }) => m.value || m).join(', ')}`;
            } else {
              queueItem.error = `${mandeError.body.message}`;
            }
          } else if (mandeError.body?.error?.errors) {
            if (Array.isArray(mandeError.body.error.errors)) {
              const errors = Object.values(mandeError.body.error.errors).map(
                (err) => (err as { message: string }).message
              );
              queueItem.error = `${mandeError.body.error._message}: ${errors.join(', ')}`;
            } else {
              queueItem.error = `${mandeError.body.error._message}: ${mandeError.body.error.errors}`;
            }
          }
        }

        if ((e as MandeError).response) {
          track('reading_submit_failed', {
            account_id: String(queueItem.state.selectedAccount?._id || ''),
            error: String(queueItem.error || '').slice(0, 200)
          });
        }
      }
    }
  }
});
